import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, formatDuration, isToday } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';
import { $path } from 'safe-routes';
import { matchPath } from 'react-router';
import { z as zv4 } from 'zod/v4';
import { z as zv3 } from 'zod';

import type { components } from '~/api/openapi';

export type SupportedLocale = 'en' | 'ja';

export function getDateFnsLocale(locale: SupportedLocale = 'en') {
  switch (locale) {
    case 'ja':
      return ja;
    case 'en':
    default:
      return enUS;
  }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeRedirect(to: string | null | undefined, defaultRedirect = '/') {
  if (!to || typeof to !== 'string') {
    return defaultRedirect;
  }
  if (!to.startsWith('/') || to.startsWith('//')) {
    return defaultRedirect;
  }
  return to;
}

export function formatDateTimeRelative(date: Date | number | string, locale?: SupportedLocale) {
  const d = typeof date === 'string' ? new Date(date) : date;
  const dateFnsLocale = getDateFnsLocale(locale);
  return isToday(d)
    ? formatDistanceToNow(d, { addSuffix: true, locale: dateFnsLocale })
    : format(d, 'MMM d, yyyy', { locale: dateFnsLocale });
}

export function formatSubmissionTime(date: Date | number | string, locale?: SupportedLocale) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'MMM do, yyyy HH:mm:ss', { locale: getDateFnsLocale(locale) });
}

export function formatDateLong(date: Date | number | string, locale?: SupportedLocale) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'MMMM d, yyyy', { locale: getDateFnsLocale(locale) });
}

export function createNextPath(requestUrl: string) {
  const url = new URL(requestUrl);

  let nextSearchParams;
  if (matchRedirectablePath(url.pathname)) {
    nextSearchParams = new URLSearchParams({ next: url.pathname + url.search });
  }

  return $path('/login', nextSearchParams);
}

function matchRedirectablePath(currentPath: string) {
  const redirectableRoute = [
    '/creator/journeys/*',
    '/journeys/:journeyId/stages/:stageId/modules/:moduleId/*',
    '/learners/*',
    '/auth/cli/authorize',
  ];

  return redirectableRoute.some(path => Boolean(matchPath({ path }, currentPath)));
}

export const URL_PROTOCOL_REGEX = /^(ht|f)tp(s?)(:?)$/;
export const URL_HOSTNAME_REGEX =
  /^(www\.)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-z]{2,63}(\/[-a-zA-Z0-9@:%_+.~#?&/=]*)?$/i;

export function validateUrl(errorMessage: string) {
  /**
   * Matches:
   * https://www.subdomain.domain.tld/path
   * http://www.sudomain.domain.tld/path
   * www.subdomain.domain.tld/path
   * subdomain.domain.tld/path
   * subdomain.domain.tld
   * domain.tld
   *
   * NOTE: Based https://www.rfc-editor.org/rfc/rfc1034 TLDs can be up to 63 characters long
   */
  return zv4.url({
    error: errorMessage,
    protocol: URL_PROTOCOL_REGEX,
    hostname: URL_HOSTNAME_REGEX,
  });
}

export function appendProtocolToUrl(url: string) {
  if (!url) {
    return '';
  }

  return /^(?:(ht|f)tp(s?):\/\/)/i.test(url) ? url : `https://${url}`;
}

export const VALID_IMAGE_URL_REGEX = /https:\/\/imagedelivery\.net\/([^/]+)\/([^/]+)/;

// Meant to be used only for files using zod/v3, primarily @conform-to/react forms.
// Otherwise, use validateUrl instead.
export function validateImageUrl(errorMessage: string) {
  return zv3
    .string()
    .trim()
    .refine(url => VALID_IMAGE_URL_REGEX.test(url), errorMessage);
}

export function getSectionImage(
  sectionType: components['schemas']['LearnerSectionDetailDto']['sectionType'] = 'RichContent'
) {
  const path = '/assets/sectionType';
  switch (sectionType) {
    case 'StickyNote':
      return `${path}/sticky-notes.svg`;
    case 'Quiz':
      return `${path}/quiz.svg`;
    case 'ReviewAndComment':
      return `${path}/review-comment.svg`;
    case 'UploadContent':
      return `${path}/upload.svg`;
    default:
      return `${path}/default.svg`;
  }
}

export function isQuestionBlock(
  blockType?: components['schemas']['PublishBlockTarget']['blockType']
) {
  return ['MultipleChoiceQuestion', 'Slider', 'FillInBlank', 'Ranking', 'Matching'].includes(
    blockType || ''
  );
}

// For viewing Glossary items
const QUERY_PARAM_SLUG = 'glossary';
const QUERY_PARAM_LANG = 'glossaryLang';

export function setGlossaryQueryParams(existingParams: URLSearchParams, { slug = '', lang = '' }) {
  if (slug) {
    existingParams.set(QUERY_PARAM_SLUG, slug);
  }
  if (lang) {
    existingParams.set(QUERY_PARAM_LANG, lang);
  }
  return existingParams;
}

export function deleteGlossaryQueryParams(existingParams: URLSearchParams) {
  existingParams.delete(QUERY_PARAM_SLUG);
  existingParams.delete(QUERY_PARAM_LANG);
  return existingParams;
}

/** Query key for opening the learner notifications sheet from a URL (e.g. email deep links). */
export const NOTIFICATIONS_SHEET_QUERY_PARAM = 'notifications';

/** Canonical value for {@link NOTIFICATIONS_SHEET_QUERY_PARAM} in outbound links (emails, push, etc.). */
export const NOTIFICATIONS_SHEET_OPEN_VALUE = '1';

export function notificationsSheetSearchParamIndicatesOpen(value: string | null): boolean {
  if (value === null) return false;
  const v = value.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'open';
}

/** Returns a copy of `existingParams` with the notifications sheet open flag set (immutable). */
export function appendNotificationsSheetOpenQueryParams(existingParams: URLSearchParams) {
  const next = new URLSearchParams(existingParams);
  next.set(NOTIFICATIONS_SHEET_QUERY_PARAM, NOTIFICATIONS_SHEET_OPEN_VALUE);
  return next;
}

export function deleteNotificationsSheetQueryParams(existingParams: URLSearchParams) {
  existingParams.delete(NOTIFICATIONS_SHEET_QUERY_PARAM);
  return existingParams;
}

/**
 * Format file size in bytes to human-readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (B, KB, or MB)
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function toTotalMinutes(hours: number, minutes: number): number {
  return hours * 60 + minutes;
}

export function fromTotalMinutes(totalMinutes: number | null | undefined): {
  hours: number;
  minutes: number;
} {
  const total = totalMinutes || 0;
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/**
 * Checks if a string contains Japanese characters (Hiragana, Katakana, or Kanji).
 * Used to determine name display order.
 */
export function containsJapanese(text: string): boolean {
  // \u3040-\u309F: Hiragana
  // \u30A0-\u30FF: Katakana
  // \u4E00-\u9FFF: Kanji (CJK Unified Ideographs)
  return /[\u3040-\u30FF\u4E00-\u9FFF]/.test(text);
}

/**
 * Formats a display name from first and last name components.
 * For names containing Japanese characters, displays as "LastName FirstName".
 * For other names, displays as "FirstName LastName".
 */
export function formatDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';

  if (!first && !last) {
    return '';
  }

  if (!first) return last;
  if (!last) return first;

  const fullName = `${first}${last}`;
  if (containsJapanese(fullName)) {
    return `${last} ${first}`;
  }

  return `${first} ${last}`;
}

type UserDto = components['schemas']['UserDto'];

export function getDisplayName(user: UserDto): string {
  const displayName = formatDisplayName(user.firstName, user.lastName);

  if (!displayName) {
    return user.email.split('@')[0] || 'User';
  }

  return displayName;
}

/**
 * Format duration in minutes to human-readable format
 * @param minutes - Duration in minutes
 * @param locale - The locale to use for formatting ('en' or 'ja'). Defaults to 'en'.
 * @returns Formatted string (e.g., '1 hour 20 minutes') or empty string for null/undefined/0
 */
export function formatDurationMinutes(
  minutes: number | null | undefined,
  locale?: SupportedLocale
): string {
  if (!minutes) return '';
  return formatDuration(
    { hours: Math.floor(minutes / 60), minutes: minutes % 60 },
    { locale: getDateFnsLocale(locale) }
  );
}
